import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import publicApi from '../services/publicApi';

const BRAND_BLUE = '#1e3a5f';

// =====================================================
// VELD-RENDERER (HTML, geen PDF-overlay)
// =====================================================
const VeldRenderer = ({ field, value, onChange, readonly }) => {
  const baseInputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: 15,
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    background: readonly ? '#f8fafc' : 'white',
    color: '#1e293b',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    fontSize: 14,
    fontWeight: 600,
    color: '#334155',
    marginBottom: 6
  };

  const wrapper = (inner) => (
    <div style={{ marginBottom: 18 }}>
      <label style={labelStyle}>
        {field.label}
        {field.verplicht && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
      </label>
      {inner}
    </div>
  );

  switch (field.type) {
    case 'textarea':
      return wrapper(
        <textarea
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          disabled={readonly}
          rows={3}
          style={{ ...baseInputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
      );
    case 'checkbox':
      return (
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: readonly ? 'default' : 'pointer' }}>
            <input
              type="checkbox"
              checked={value === 'true' || value === true}
              onChange={(e) => onChange(field.id, e.target.checked ? 'true' : 'false')}
              disabled={readonly}
              style={{ width: 18, height: 18, marginRight: 10 }}
            />
            <span style={{ fontSize: 14, color: '#334155' }}>
              {field.label}
              {field.verplicht && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
            </span>
          </label>
        </div>
      );
    case 'date':
      return wrapper(
        <input
          type="date"
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          disabled={readonly}
          style={baseInputStyle}
        />
      );
    case 'time':
      return wrapper(
        <input
          type="time"
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          disabled={readonly}
          style={baseInputStyle}
        />
      );
    case 'signature':
      // In invullen-mode: hint dat dit later getekend wordt. Bij bekijken: 'getekend' tonen.
      return wrapper(
        <div style={{ ...baseInputStyle, fontStyle: 'italic', color: '#64748b' }}>
          {value && value !== 'false' ? '✓ Getekend' : '(Wordt later ondertekend)'}
        </div>
      );
    case 'foto': {
      const isPhoto = typeof value === 'string' && value.startsWith('[FOTO]:');
      const apiBase = window.location.origin;
      const tokenFromUrl = window.location.pathname.split('/').pop();
      const photoUrl = isPhoto ? `${apiBase}/api/forms-public/${tokenFromUrl}/foto/${field.id}?t=${Date.now()}` : null;

      const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('foto', file);
        formData.append('field_id', String(field.id));
        try {
          const r = await fetch(`/api/forms-public/${tokenFromUrl}/upload-foto`, {
            method: 'POST',
            body: formData
          });
          const data = await r.json();
          if (data?.success) {
            onChange(field.id, data.waarde);
          } else {
            alert('Upload mislukt: ' + (data?.message || 'Onbekend'));
          }
        } catch (err) {
          alert('Upload fout: ' + err.message);
        }
      };

      return wrapper(
        <div>
          {isPhoto && photoUrl && (
            <div style={{ marginBottom: 8 }}>
              <img src={photoUrl} alt="Geuploade foto" style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 6, border: '1px solid #cbd5e1' }} />
            </div>
          )}
          {!readonly && (
            <label style={{ display: 'inline-block', padding: '10px 16px', background: '#1e3a5f', color: 'white', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              📷 {isPhoto ? 'Foto vervangen' : 'Foto uploaden'}
              <input type="file" accept="image/*" capture="environment" onChange={handleFile} style={{ display: 'none' }} />
            </label>
          )}
        </div>
      );
    }
    case 'text':
    default:
      return wrapper(
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          disabled={readonly}
          style={baseInputStyle}
        />
      );
  }
};

// =====================================================
// SIGNATURE CANVAS
// =====================================================
const SignaturePad = ({ onCapture }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
  }, []);

  const getCoords = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const end = () => setIsDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onCapture(null);
  };

  const capture = () => {
    if (!hasSignature) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onCapture(dataUrl);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        onMouseDown={start}
        onMouseMove={draw}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={draw}
        onTouchEnd={end}
        style={{
          width: '100%',
          height: 180,
          border: '2px dashed #cbd5e1',
          borderRadius: 8,
          background: 'white',
          touchAction: 'none',
          cursor: 'crosshair'
        }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button onClick={clear} type="button" style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}>
          Wissen
        </button>
        <button onClick={capture} type="button" disabled={!hasSignature} style={{ padding: '8px 16px', background: hasSignature ? BRAND_BLUE : '#cbd5e1', color: 'white', border: 'none', borderRadius: 6, cursor: hasSignature ? 'pointer' : 'not-allowed', fontWeight: 600 }}>
          Bevestig handtekening
        </button>
      </div>
    </div>
  );
};

// =====================================================
// HOOFDCOMPONENT
// =====================================================
export default function MagicLinkForm() {
  const { actie: actieParam, token } = useParams();
  const [data, setData] = useState(null);
  const [waardes, setWaardes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // 'submitted' | 'signed'
  const [signerName, setSignerName] = useState('');
  const [capturedSig, setCapturedSig] = useState(null);

  // Load form
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await publicApi.get(`/forms-public/${token}`);
        if (cancel) return;
        if (r.data?.success) {
          setData(r.data);
          setWaardes(r.data.values || {});
          if (r.data.actie !== actieParam) {
            console.warn(`URL actie (${actieParam}) komt niet overeen met token actie (${r.data.actie}). Token wint.`);
          }
        } else {
          setError(r.data?.message || 'Onbekende fout');
        }
      } catch (e) {
        const msg = e.response?.data?.message || 'Kan formulier niet laden';
        const code = e.response?.data?.code;
        setError(msg);
        setErrorCode(code);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [token, actieParam]);

  // Auto-save (debounced)
  const saveTimer = useRef(null);
  const handleChange = useCallback((fieldId, val) => {
    setWaardes((prev) => ({ ...prev, [fieldId]: val }));
    if (data?.actie !== 'invullen' || data?.readonly) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await publicApi.put(`/forms-public/${token}/values`, {
          values: { [fieldId]: val }
        });
      } catch (e) {
        console.error('Auto-save fout', e);
      } finally {
        setSaving(false);
      }
    }, 800);
  }, [token, data]);

  // Submit (invullen)
  const handleSubmit = async () => {
    // Verplichte velden check
    const missing = (data.fields || []).filter(f => f.verplicht && f.type !== 'signature' && (waardes[f.id] === undefined || waardes[f.id] === '' || waardes[f.id] === null));
    if (missing.length > 0) {
      alert(`Vul eerst de verplichte velden in:\n${missing.map(f => `• ${f.label}`).join('\n')}`);
      return;
    }
    if (!window.confirm('Formulier indienen? Hierna kun je het niet meer wijzigen.')) return;
    setSubmitting(true);
    try {
      // Eerst alle huidige waardes saven (force flush)
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await publicApi.put(`/forms-public/${token}/values`, { values: waardes });
      await publicApi.post(`/forms-public/${token}/submit`);
      setDone('submitted');
    } catch (e) {
      alert('Indienen mislukt: ' + (e.response?.data?.message || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  // Sign (ondertekenen)
  const handleSign = async () => {
    if (!signerName.trim() || signerName.trim().length < 2) {
      alert('Vul je volledige naam in (min. 2 tekens).');
      return;
    }
    if (!capturedSig) {
      alert('Zet eerst je handtekening en klik "Bevestig handtekening".');
      return;
    }
    if (!window.confirm(`Formulier ondertekenen als "${signerName.trim()}"? Deze actie kan niet ongedaan gemaakt worden.`)) return;
    setSubmitting(true);
    try {
      await publicApi.post(`/forms-public/${token}/sign`, {
        naam: signerName.trim(),
        handtekening_data: capturedSig
      });
      setDone('signed');
    } catch (e) {
      alert('Ondertekenen mislukt: ' + (e.response?.data?.message || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  // ===== RENDER STATES =====
  if (loading) return <CenterMsg>Formulier laden…</CenterMsg>;

  if (error) {
    const titles = {
      EXPIRED: 'Deze link is verlopen',
      REVOKED: 'Deze link is ingetrokken',
      USED: 'Deze link is al gebruikt'
    };
    return <CenterMsg type="error" title={titles[errorCode] || 'Link niet beschikbaar'}>{error}</CenterMsg>;
  }

  if (done === 'submitted') {
    return <CenterMsg type="success" title="Bedankt!">Het formulier is succesvol ingediend.</CenterMsg>;
  }
  if (done === 'signed') {
    return <CenterMsg type="success" title="Ondertekend!">Bedankt, het formulier is ondertekend en de afzender wordt op de hoogte gesteld.</CenterMsg>;
  }

  const { actie, readonly, submission, template, bedrijf, fields, ontvanger, geldig_tot } = data;
  const geldigTotStr = geldig_tot ? new Date(geldig_tot).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  // ===== HEADER =====
  const Header = (
    <div style={{ background: BRAND_BLUE, color: 'white', padding: '20px 16px', textAlign: 'center' }}>
      {bedrijf?.logo_url
        ? <img src={bedrijf.logo_url} alt={bedrijf.naam} style={{ maxHeight: 48, maxWidth: 200, marginBottom: 8 }} />
        : <h2 style={{ margin: 0, fontSize: 22 }}>{bedrijf?.naam || 'WOMsys Forms'}</h2>}
      <div style={{ marginTop: 8, fontSize: 14, opacity: 0.85 }}>{template?.naam}</div>
    </div>
  );

  // ===== INHOUD =====
  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: 'system-ui, -apple-system, Arial, sans-serif' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', background: 'white', minHeight: '100vh', boxShadow: '0 0 24px rgba(0,0,0,0.06)' }}>
        {Header}

        <div style={{ padding: 20 }}>
          {/* Actie banner */}
          <div style={{ background: actie === 'ondertekenen' ? '#fef2f2' : '#eff6ff', padding: 12, borderRadius: 8, marginBottom: 20, fontSize: 14, color: actie === 'ondertekenen' ? '#991b1b' : '#1e40af' }}>
            {actie === 'invullen'     && <>📝 Vul dit formulier in en klik onderaan op <strong>Indienen</strong>.</>}
            {actie === 'ondertekenen' && <>✍️ Lees het formulier zorgvuldig door en zet je handtekening onderaan.</>}
            {actie === 'bekijken'     && <>👁️ Je bekijkt dit formulier ter referentie. Wijzigen is niet mogelijk.</>}
          </div>

          {/* Velden — of PDF in bekijken-mode met gegenereerde PDF */}
          {actie === 'bekijken' && submission?.heeft_gegenereerde_pdf ? (
            <div>
              <iframe
                src={`/api/forms-public/${token}/pdf`}
                title="Document"
                style={{
                  width: '100%',
                  height: '70vh',
                  border: '1px solid #cbd5e1',
                  borderRadius: 8,
                  marginBottom: 12,
                  background: 'white'
                }}
              />
              <a
                href={`/api/forms-public/${token}/pdf?download=1`}
                style={{
                  display: 'inline-block',
                  padding: '10px 18px',
                  background: '#10b981',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 14
                }}
              >
                📥 Download PDF
              </a>
            </div>
          ) : (
            (fields || []).map((field) => (
              <VeldRenderer
                key={field.id}
                field={field}
                value={waardes[field.id]}
                onChange={handleChange}
                readonly={readonly || actie !== 'invullen'}
              />
            ))
          )}

          {/* Save indicator */}
          {actie === 'invullen' && (
            <div style={{ fontSize: 12, color: '#64748b', textAlign: 'right', marginBottom: 12 }}>
              {saving ? 'Opslaan...' : 'Wijzigingen worden automatisch opgeslagen'}
            </div>
          )}

          {/* Action button — invullen */}
          {actie === 'invullen' && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{ width: '100%', padding: '14px', background: BRAND_BLUE, color: 'white', fontSize: 16, fontWeight: 600, border: 'none', borderRadius: 8, cursor: submitting ? 'wait' : 'pointer', marginTop: 12 }}
            >
              {submitting ? 'Bezig met indienen...' : '✓ Formulier indienen'}
            </button>
          )}

          {/* Action area — ondertekenen */}
          {actie === 'ondertekenen' && (
            <div style={{ marginTop: 24, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 12px', color: BRAND_BLUE }}>Ondertekenen</h3>

              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Volledige naam *</label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Bv. Jan de Vries"
                style={{ width: '100%', padding: '10px 12px', fontSize: 15, border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 16, boxSizing: 'border-box' }}
              />

              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Handtekening *</label>
              <SignaturePad onCapture={setCapturedSig} />

              <button
                onClick={handleSign}
                disabled={submitting || !capturedSig || !signerName.trim()}
                style={{ width: '100%', padding: '14px', background: capturedSig && signerName.trim() && !submitting ? BRAND_BLUE : '#cbd5e1', color: 'white', fontSize: 16, fontWeight: 600, border: 'none', borderRadius: 8, cursor: capturedSig && signerName.trim() ? 'pointer' : 'not-allowed', marginTop: 16 }}
              >
                {submitting ? 'Bezig...' : '✍️ Ondertekenen'}
              </button>
            </div>
          )}

          {/* Footer */}
          <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid #e2e8f0', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
            {geldigTotStr && <div>Deze link is geldig tot {geldigTotStr}</div>}
            <div style={{ marginTop: 6 }}>Verzonden via <strong style={{ color: BRAND_BLUE }}>WOMsys Forms</strong></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// HELPER: gecentreerd bericht (loading/error/success)
// =====================================================
function CenterMsg({ type, title, children }) {
  const colors = {
    error:   { bg: '#fef2f2', border: '#fecaca', title: '#991b1b' },
    success: { bg: '#f0fdf4', border: '#bbf7d0', title: '#166534' }
  };
  const c = colors[type] || { bg: 'white', border: '#e2e8f0', title: BRAND_BLUE };
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', padding: 20, fontFamily: 'system-ui, -apple-system, Arial, sans-serif' }}>
      <div style={{ background: c.bg, border: `1px solid ${c.border}`, padding: 32, borderRadius: 12, maxWidth: 420, textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
        {title && <h2 style={{ margin: '0 0 12px', color: c.title }}>{title}</h2>}
        <div style={{ color: '#475569', fontSize: 15, lineHeight: 1.5 }}>{children}</div>
      </div>
    </div>
  );
}
